import { createI18n } from 'vue-i18n'
import zhCN from './locales/zh-CN.json'

/**
 * i18n。
 *
 * 語言代碼直接用 BCP 47（`zh-CN`），與網址片段、`<html lang>`、
 * 語言包檔名三者一致 —— T1 用了 `zhCN` / `zh-cn` / `zhCN.json` 三種寫法互轉，
 * 中間靠 `formatLocaleCode()` 做正規化，是不必要的複雜度。
 */

export const SUPPORTED_LOCALES = ['zh-CN', 'en-US'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

/**
 * 簡中優先。
 *
 * 用 `satisfies` 而非 `: Locale` 標註 —— 後者會把型別放寬成整個聯集，
 * 使 `Exclude<Locale, typeof DEFAULT_LOCALE>` 變成 `never`。
 */
export const DEFAULT_LOCALE = 'zh-CN' satisfies Locale

const STORAGE_KEY = 'locale'

export function isSupportedLocale(value: unknown): value is Locale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as Locale)
}

/**
 * 決定首次進站要用哪個語言。
 *
 * 優先序：**使用者上次的選擇 → 簡中**。網址不參與 ——
 * 語系是偏好設定，不是資源位置。
 *
 * 刻意不看 `navigator.languages`。這個站的使用者是中文的，而瀏覽器語言
 * 常常不是使用者選的 —— 公司配的機器、網咖、手機出廠設定都可能是英文，
 * 於是一大票中文使用者第一眼看到英文站，還得自己找語言選單。
 * 猜錯的代價遠大於猜對的好處，所以不猜：一律簡中，要英文的人自己切一次，
 * 切完就記在 localStorage 裡。
 */
export function resolveInitialLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  return isSupportedLocale(stored) ? stored : DEFAULT_LOCALE
}

type MessageSchema = typeof zhCN

/**
 * 日期格式。
 *
 * 必須明確定義 —— vue-i18n 沒有內建的具名格式，
 * 呼叫 `d(date, 'short')` 而沒有對應設定時會**靜默回傳空字串**，
 * 不會報錯，很容易在畫面上出現空白而查不到原因。
 */
const datetimeFormats = {
  'zh-CN': {
    short: { year: 'numeric', month: '2-digit', day: '2-digit' },
    long: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    // 注單要對到秒 —— 客服拿著單號問遊戲商時，分鐘級的時間對不出是哪一筆。
    // `hour12: false` 是必要的：同一頁同時出現 24 小時制與 AM/PM 讀起來像兩份資料
    stamp: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    },
  },
  'en-US': {
    short: { year: 'numeric', month: 'short', day: 'numeric' },
    long: { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' },
    stamp: {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    },
  },
} as const

/**
 * 數字格式。同樣必須明確定義，否則 `n(value, ...)` 回空字串。
 *
 * `amount` 是**唯一**的金額格式：只有數字，固定兩位小數，不標幣別。
 * 站上的金額是平台幣 —— 平台自己的點數，沒有「元／分」這種幣別單位
 * （見 `contracts/wallet.contract.ts` 的「金額」段）。標上 `¥` 會讓人
 * 以為那是一個真的人民幣帳戶，而它不是。
 *
 * 曾經有第二個帶 `¥` 的 `currency` 格式給出入款流程用，已經移除：
 * 同一筆錢在 header 與存款頁顯示成兩種單位，比不標單位更難懂。
 * 「`100` 到底是多少」由畫面上的文案回答，不是靠數字前面掛符號。
 */
const numberFormats = {
  'zh-CN': {
    amount: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  },
  'en-US': {
    amount: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  },
} as const

// 預設語言包同步打包（首屏一定用得到），其餘語言按需載入。
// 明確標註 Locale 為可用語言集合 —— 否則會被推論成只有 `'zh-CN'`
// （因為初始 messages 只有一個 key），導致切成 en-US 時型別報錯。
export const i18n = createI18n<[MessageSchema], Locale, false>({
  legacy: false,
  // 明確開啟，讓 template 能直接用 $t / $d / $n。
  // 不寫的話會依版本預設值而異，升級時容易無聲失效。
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  messages: { 'zh-CN': zhCN } as Record<Locale, MessageSchema>,
  datetimeFormats: datetimeFormats as never,
  numberFormats: numberFormats as never,
})

const loaded = new Set<Locale>([DEFAULT_LOCALE])

/**
 * 非預設語言的載入器。
 *
 * 用明確的 map 而非 `import(\`./locales/${locale}.json\`)` 模板字串：
 * 後者會把已經靜態引入的預設語言也納入動態範圍，
 * rollup 會警告該模組無法拆分成獨立 chunk。列舉寫法讓打包結果乾淨且可預測。
 */
const LOCALE_LOADERS: Record<
  Exclude<Locale, typeof DEFAULT_LOCALE>,
  () => Promise<{ default: MessageSchema }>
> = {
  'en-US': () => import('./locales/en-US.json'),
}

/**
 * 只把偏好寫進 localStorage，不動畫面。
 *
 * 給「切語系後整頁重載」用（見 `composables/useLocale.ts`）：重載前沒有必要
 * 先下載語言包再切 —— 那份 messages 下一秒就跟著整個 app 一起被丟掉。
 */
export function persistLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale)
}

/**
 * 套用語言。需要時才下載語言包，**載入完成後**才切換 ——
 * 先切再載會讓畫面短暫顯示原始 i18n key。
 *
 * 一律同步更新 `<html lang>`：影響螢幕閱讀器發音、瀏覽器翻譯提示、
 * 以及 CJK 的字型與斷行規則。T1 完全沒處理這件事。
 *
 * **不對外開放**：唯一的呼叫者是下面的 `initLocale()`。畫面上切語系走的是
 * 「寫偏好 ＋ 整頁重載」（見 `composables/useLocale.ts`）—— 後端依語系回的
 * 內容（遊戲名稱、公告、優惠）不會因為這裡換了 locale 就自己重抓。
 */
async function setLocale(locale: Locale): Promise<void> {
  if (!loaded.has(locale)) {
    const load = LOCALE_LOADERS[locale as Exclude<Locale, typeof DEFAULT_LOCALE>]
    const messages = await load()
    i18n.global.setLocaleMessage(locale, messages.default)
    loaded.add(locale)
  }

  i18n.global.locale.value = locale
  localStorage.setItem(STORAGE_KEY, locale)
  document.documentElement.lang = locale
}

/**
 * 應用啟動時套用使用者的語言偏好。
 *
 * 必須在 `app.mount()` **之前**呼叫：晚一步的話首屏會先用預設語言渲染，
 * 然後閃一下才變成使用者實際偏好的語言。
 */
export async function initLocale(): Promise<void> {
  const locale = resolveInitialLocale()

  // 已經是預設語言就不必重新載入語言包，只要對齊 `<html lang>`
  if (locale === DEFAULT_LOCALE) {
    document.documentElement.lang = locale
    return
  }

  await setLocale(locale)
}
